import { useQuery, useQueryClient } from "@tanstack/react-query";
import { URLS } from "@services/urls";
import { useState } from "react";
import api from "@services/api";

// Centralized Header Builder
const buildHeaders = () => ({
  "Content-Type": "application/json",
  accept: "application/json",
});

const getUsers = async () => {
  const response = await api.get(URLS.users.USER_ALL, {
    headers: buildHeaders(),
  });
  return response.data;
};

const getUserDetails = async (userId) => {
  if (!userId) {
    throw new Error("No userId provided to getUserDetails");
  }
  const uri = `${URLS.users.USER_DETAILS}${userId}/details`;
  const response = await api.get(uri, { headers: buildHeaders() });
  return response.data;
};

export const useUsersSettingService = (userId) => {
  const [error, setError] = useState(null);
  const queryClient = useQueryClient();

  // 🟢 Hooks must be called every time — control behavior with `enabled`
  const userList = useQuery({
    queryKey: ["users"],
    queryFn: getUsers,
    enabled: !!userId, // prevent refetching when logged out
    onError: (error) => {
      console.error("Error fetching user list:", error);
      setError(error);
    },
  });

  const userDetails = useQuery({
    queryKey: ["userDetails", userId],
    queryFn: () => getUserDetails(userId),
    enabled: !!userId,
    staleTime: 1000 * 60 * 5, // 5 minutes

    onError: (error) => {
      console.error(`Error fetching user details for ID ${userId}:`, error);
      setError(error);
    },
  });

  /* const refetchUserDetails = () => {
    if (userId) {
      queryClient.invalidateQueries(["userDetails", userId]);
    }
  }; */

  const refetchUserDetails = async () => {
    if (!userId) return;
    // Option A: force immediate refetch and await it
    return queryClient.refetchQueries({ queryKey: ["userDetails", userId] });
    // Option B: just invalidate (no need to await)
    // return queryClient.invalidateQueries({ queryKey: ["userDetails", userId] });
  };

  return {
    users: userList?.data || null,
    userDetails: userDetails?.data || null,
    isUserListLoading: userList?.isLoading || false,
    isUserDetailsLoading: userDetails?.isLoading || false,
    refetchUsers: userList.refetch,
    refetchUserDetails,
    onError: error,
  };
};
